import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import {
  BORDER_RADIUS_SMALL,
  FONTS,
  SCREEN_PADDING,
  TYPOGRAPHY,
} from '@/styles';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { Text, View } from 'react-native';
import { useCheckInContext } from '@/contexts/check-in-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  invoiceUUID: string;
  isVisible: boolean;
  onClose: (shouldNavigateBack: boolean) => void;
}

export const OrderCheckInSummary: React.FC<Props> = ({
  invoiceUUID,
  isVisible,
  onClose,
}) => {
  const [isCreditRequestInfoVisible, setIsCreditRequestInfoVisible] =
    useState(false);

  const [styles, theme] = useStyles();

  const {
    completeCheckIn,
    currencyCode,
    problematicItemsMetrics,
    totalShippedQuantity,
  } = useCheckInContext();
  const { showErrorToast } = useToasts();

  const totalProblematicItemsAmount =
    (problematicItemsMetrics?.missing ?? 0) +
    (problematicItemsMetrics?.withIssues ?? 0);

  const receivedPercentage = useMemo(() => {
    const amountOfItemsMissing = problematicItemsMetrics?.missing ?? 0;
    if (amountOfItemsMissing && totalShippedQuantity > 0) {
      const quantityDifference = totalShippedQuantity - amountOfItemsMissing;
      return Math.round((quantityDifference / totalShippedQuantity) * 100);
    }
    return 100;
  }, [problematicItemsMetrics?.missing, totalShippedQuantity]);

  const noIssuesPercentage = useMemo(() => {
    if (totalProblematicItemsAmount && totalShippedQuantity > 0) {
      const quantityDifference =
        totalShippedQuantity - totalProblematicItemsAmount;
      return Math.round((quantityDifference / totalShippedQuantity) * 100);
    }
    return 0;
  }, [totalProblematicItemsAmount, totalShippedQuantity]);

  const hasIssuesPercentage = receivedPercentage - noIssuesPercentage;

  const completeCheckInWithIssues = async () => {
    try {
      await completeCheckIn();
      setIsCreditRequestInfoVisible(true);
    } catch {
      onClose(false);
      showErrorToast();
    }
  };

  return (
    <BaseActionSheet
      isVisible={isVisible}
      onClose={() => {
        onClose(isCreditRequestInfoVisible);
      }}
    >
      {isCreditRequestInfoVisible ? (
        <>
          <View style={styles.creditRequestInfoWrapper}>
            <Text style={styles.actionTitle}>
              {i18n.t('check_in.credit_request.title')}
            </Text>
            <Text
              numberOfLines={2}
              style={[TYPOGRAPHY.p, { color: theme.sk.color.text.secondary }]}
            >
              {i18n.t('check_in.credit_request.description')}
            </Text>
          </View>

          <Button
            onPress={() => {
              onClose(isCreditRequestInfoVisible);
            }}
            trackingData={{
              name: 'check_in_credit_request_button_ok',
              properties: {
                invoiceUUID,
              },
            }}
            type="secondary"
          >
            {i18n.t('got_it')}
          </Button>
        </>
      ) : (
        <>
          <Text style={styles.summaryTitle}>
            {i18n.t('check_in.summary.title')}
          </Text>
          <View style={styles.summaryWrapper}>
            <View style={styles.summaryHighlightsWrapper}>
              <View style={styles.summaryHighlight}>
                <Text style={TYPOGRAPHY.xsmall}>{i18n.t('received')}</Text>
                <Text
                  style={styles.summaryHighlightValue}
                >{`${receivedPercentage}%`}</Text>
              </View>
              <View style={styles.summaryHighlight}>
                <Text style={TYPOGRAPHY.xsmall}>
                  {i18n.t('check_in.summary.missing_or_issues')}
                </Text>
                <Text style={styles.summaryHighlightValue}>
                  {totalProblematicItemsAmount === 1
                    ? i18n.t('unit_count', {
                        count: totalProblematicItemsAmount,
                      })
                    : i18n.t('unit_count_plural', {
                        count: totalProblematicItemsAmount,
                      })}
                </Text>
              </View>
              <View style={styles.summaryHighlight}>
                <Text style={TYPOGRAPHY.xsmall}>
                  {i18n.t('check_in.summary.est_credit')}
                </Text>
                <Text style={styles.summaryHighlightValue}>
                  {formatCurrency(
                    problematicItemsMetrics?.cost ?? 0,
                    currencyCode,
                  )}
                </Text>
              </View>
            </View>
            <View collapsable={false}>
              <View style={styles.summaryBarWrapper}>
                <View
                  style={[
                    styles.summaryBarReceived,
                    { width: `${noIssuesPercentage}%` },
                  ]}
                />
                <View
                  style={[
                    styles.summaryBarWithIssues,
                    { width: `${hasIssuesPercentage}%` },
                  ]}
                />
              </View>
              <View style={styles.summaryLabels}>
                <View style={styles.summaryLabelWrapper}>
                  <View
                    style={[
                      styles.summaryLabel,
                      { backgroundColor: theme.sk.color.bg.highlight.primary },
                    ]}
                  />
                  <Text style={styles.summaryLabelText}>
                    {i18n.t('check_in.summary.no_issues')}
                  </Text>
                </View>
                {Boolean(problematicItemsMetrics?.withIssues) ? (
                  <View style={styles.summaryLabelWrapper}>
                    <View
                      style={[
                        styles.summaryLabel,
                        {
                          backgroundColor:
                            theme.sk.color.icon.warning.secondary,
                        },
                      ]}
                    />
                    <Text style={styles.summaryLabelText}>
                      {i18n.t('check_in.filter.has_issues')}
                    </Text>
                  </View>
                ) : null}
                <View style={styles.summaryLabelWrapper}>
                  <View
                    style={[
                      styles.summaryLabel,
                      { backgroundColor: theme.sk.color.bg.secondary.hover },
                    ]}
                  />
                  <Text style={styles.summaryLabelText}>
                    {i18n.t('check_in.summary.missing')}
                  </Text>
                </View>
              </View>
            </View>
          </View>
          <Button
            onPress={completeCheckInWithIssues}
            trackingData={{
              name: 'submit_check_in_button',
              properties: {
                invoiceUUID,
              },
            }}
          >
            {i18n.t('submit')}
          </Button>
        </>
      )}
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  actionTitle: {
    ...FONTS.primarySemiBold,
    fontSize: 21,
    lineHeight: 30,
    marginBottom: 8,
  },
  creditRequestInfoWrapper: {
    marginBottom: 24,
  },
  summaryBarReceived: {
    backgroundColor: theme.sk.color.bg.highlight.primary,
    height: 4,
  },
  summaryBarWithIssues: {
    backgroundColor: theme.sk.color.icon.warning.secondary,
    height: 4,
  },
  summaryBarWrapper: {
    backgroundColor: theme.sk.color.bg.secondary.hover,
    flexDirection: 'row',
    height: 4,
    marginBottom: 16,
  },
  summaryHighlight: {
    gap: 2,
  },
  summaryHighlightsWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  summaryHighlightValue: {
    ...FONTS.primaryBold,
    fontSize: theme.fontSizes.large,
  },
  summaryLabel: {
    height: 8,
    width: 8,
  },
  summaryLabels: {
    flexDirection: 'row',
    gap: 32,
  },
  summaryLabelText: {
    ...FONTS.primary,
    color: theme.sk.color.text.secondary,
    fontSize: theme.fontSizes.xsmall,
  },
  summaryLabelWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  summaryTitle: {
    ...FONTS.primarySemiBold,
    fontSize: 21,
    lineHeight: 30,
    marginBottom: 16,
  },
  summaryWrapper: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: BORDER_RADIUS_SMALL,
    borderWidth: 1,
    gap: 24,
    marginBottom: 24,
    padding: SCREEN_PADDING,
  },
}));
